import { Api, ArrayUtil, ModelRow } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, Label, Layout, LayoutLoadErrorEvent, Panel, PanelProcessLastRun, PanelProcessLastRunVariant, PanelProps,
    ReportData, SlideoutDecorator, Snackbar, StepEvent, Tab
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutReverseTransferSettlementStepper } from "./autogen/AutogenLayoutReverseTransferSettlementStepper";
import { OrderPayables } from "./OrderPayables";
import { BackOfficeReportPreview } from "./BackOfficeReportPreview";
import {
    ReverseTransferSettlementWarningsAndExceptionsTable
} from "./ReverseTransferSettlementWarningsAndExceptionsTable";

export interface ReverseTransferSettlementStepperProps extends PanelProps {
    selectedRows: ModelRow[],
    processCount: number,
    validMovementsCount: number,
    settlementsReversed: boolean,
    onClose: (canceled: boolean, updatedData?: ModelRow) => void,
    doAfterSlideIn: (layout: OrderPayables) => void,
    processLog: PanelProcessLastRun
}

const INDEX_EDIT_LIST = 0;
const INDEX_POST = 1;

export class ReverseTransferSettlementStepper extends AutogenLayoutReverseTransferSettlementStepper {
    private reportPreviewEditResults: BackOfficeReportPreview;
    private reportPreviewPostResults: BackOfficeReportPreview;

    doBeforeStep(event: StepEvent, buttonContinue: Button, props: Partial<ReverseTransferSettlementStepperProps>) {
        this.doBeforeStepLayoutDisplay();

        if (buttonContinue.visible) {
            buttonContinue.visible = event.newIndex == INDEX_POST ? false : true && !props.settlementsReversed;
        }

        if (INDEX_EDIT_LIST == event.newIndex) {
            buttonContinue.visible = true;
            if ((INDEX_POST == event.oldIndex && props.settlementsReversed) || props.validMovementsCount <= 0) {
                event.preventDefault();
                buttonContinue.visible = false;
                return;
            }
        }

        if (INDEX_POST == event.newIndex) {
            if (this.reportPreviewEditResults.isEmpty()) {
                event.preventDefault();
                return;
            }
            if (this.reverseTransferSettlementPostStep.busy != true) {
                CommonDialogs.showYesNo(this.createPostContent(), "Post Confirmation").then(async result => {
                    if (result === true) {
                        this.reverseTransferSettlementPostStep.busy = true;
                        const postReport = await this.postReversals(props);
                        this.setupWarningsAndExceptionsLayout(props).then((warningsAndExceptionsTable) => {
                            this.displayPostResults(postReport, warningsAndExceptionsTable);
                        });
                        this.reverseTransferSettlementPostStep.busy = false;
                        await props.processLog?.refreshLastProcessedData();
                    } else {
                        this.retreatToNextStep();
                    }
                    return;
                });
            }
        }
    }

    public postReversals(props: Partial<ReverseTransferSettlementStepperProps>): Promise<any> {
        return new Promise((resolve, reject) => {
            this.busy = true;
            const selectedRows = props.selectedRows;

            Api.post("lme/settlement/reverse-transfer-from-settlement-post", {
                order_payables: selectedRows
            }).then((response) => {
                const editList = response?.data[0]?.reverse_transfer_report_data;
                if (editList && editList.length > 0) {
                    props.settlementsReversed = true;
                    if (response?.data[0]?.valid_movements_count > 0) {
                        Snackbar.showSuccessSnackbar("Settlements were successfully reversed (" + response?.data[0]?.valid_movements_count + ")");
                    }
                }
                else {
                    CommonDialogs.showMessage("No Settlements were reversed because an error was encountered. Please correct error and re-run process.",
                        "Reverse Transfer Error", {
                            wrap:true,
                            maxWidth:500,
                            panelTitleProps:{
                                backgroundColor: "primary.darker",
                                color: "primary.reverse"
                            }
                        }
                    )
                        ;
                }
                resolve(response);
            })
            .finally(() => this.busy = false);
        });
    }

    private createPostContent(): Panel {
        const result = new Panel({ margin: 0, padding: 0, scrollY: true });
        const labelTwo = new Label({ wrap: true });
        labelTwo.text = "Are you sure you would like to proceed?";
        result.add(labelTwo);
        return result;
    }

    doBeforeStepLayoutDisplay() {
        if (this.parent?.["dismissAllSnackbars"])
            this.parent["dismissAllSnackbars"]();
    }

    private setupWarningsAndExceptionsLayout(props: Partial<ReverseTransferSettlementStepperProps>):
        Promise<ReverseTransferSettlementWarningsAndExceptionsTable> {
        return new Promise((resolve, reject) => {
            const layoutPath = "lme/settlement/ReverseTransferSettlementWarningsAndExceptionsTable";
            const reverseTransferSettlementWarningsAndExceptionsTable =
                Layout.getLayout(layoutPath, { ...props }) as ReverseTransferSettlementWarningsAndExceptionsTable;
            reverseTransferSettlementWarningsAndExceptionsTable.addLayoutLoadListener(() => {
                reverseTransferSettlementWarningsAndExceptionsTable
                    .addLoadErrorListener((event: LayoutLoadErrorEvent) => reject(event.errorMessage));
                resolve(reverseTransferSettlementWarningsAndExceptionsTable);
            });
        });
    }

    private displayEditResults(reportData: ReportData[], buttonContinue: Button,
        warningsAndExceptionsTable: ReverseTransferSettlementWarningsAndExceptionsTable) {
        this.initializeEditResultsPanel();
        const reportDataPresent = !ArrayUtil.isEmptyArray(reportData);
        const nonReportTabs = this.getNonReportTabs(warningsAndExceptionsTable);
        if (reportDataPresent || nonReportTabs.size > 0) {
            const documents = [ ...reportData, ...nonReportTabs ];
            this.reportPreviewEditResults.data = documents;
        }
        buttonContinue.visible = reportDataPresent;
    }

    private initializeEditResultsPanel() {
        if (this.reportPreviewEditResults == null) {
            this.reportPreviewEditResults = new BackOfficeReportPreview("");
            this.reverseTransferSettlementEditStep.add(this.reportPreviewEditResults);
        }
    }

    private getNonReportTabs(warningsAndExceptionsTable: ReverseTransferSettlementWarningsAndExceptionsTable): Set<Tab>
    {
        const result = new Set<Tab>();
        if (!warningsAndExceptionsTable.tableWarningsAndExceptions.isEmpty()) {
            const tab = new Tab({ id: "warningsAndExceptionsTable", paddingTop: 0 });
            const caption = "Warnings & Exceptions";
            tab.caption = caption;
            tab.fillHeight = true;
            tab.fillRow = true;
            tab.imageName = ImageName.triangleWarning;
            tab.imageColor = "error";
            tab.add(warningsAndExceptionsTable);
            result.add(tab);
        }
        return result;
    }

    private displayPostResults(postResponse: any, warningsAndExceptionsTable: ReverseTransferSettlementWarningsAndExceptionsTable) {
        const warningsAndExceptions = postResponse?.data[0]?.warning_exception_data;
        warningsAndExceptionsTable.addWarningsAndExceptions(warningsAndExceptions);
        this.initializePostResultsPanel();
        const nonReportTabs = this.getNonReportTabs(warningsAndExceptionsTable);
        const reverseTransferReportData = postResponse?.data[0]?.reverse_transfer_report_data ?? [];
        this.reportPreviewPostResults.data = [...reverseTransferReportData, ...nonReportTabs];
    }

    private initializePostResultsPanel() {
        if (this.reportPreviewPostResults == null) {
            this.reportPreviewPostResults = new BackOfficeReportPreview("");
            this.reverseTransferSettlementPostStep.add(this.reportPreviewPostResults);
        }
    }

    advanceToNextStep() {
        const nextIndex = this.reverseTransferSettlementStepper.selectedIndex + 1;
        this.reverseTransferSettlementStepper.selectedIndex = nextIndex;
    }

    retreatToNextStep() {
        const nextIndex = this.reverseTransferSettlementStepper.selectedIndex - 1;
        this.reverseTransferSettlementStepper.selectedIndex = nextIndex;
    }

    private createContinueButton(): Button {
        return new Button({
            id: "buttonContinue",
            caption: "Continue",
            backgroundColor: "primary",
            color: "primary.reverse",
            rowBreak: false,
            borderWidth: 0,
            onClick: () => {
                this.advanceToNextStep()
            }
        });
    }

    static async showAsSlideout(props: Partial<ReverseTransferSettlementStepperProps>) {
        const layoutPath = "lme/settlement/ReverseTransferSettlementStepper";
        const stepperLayout = Layout.getLayout(layoutPath, { ...props }) as ReverseTransferSettlementStepper;
        const addlComponents = [];
        const processLog = PanelProcessLastRun.create("Reverse Settlement", {
            variant: PanelProcessLastRunVariant.compact
        });
        if (processLog) {
            props.processLog = processLog;
            addlComponents.push(processLog);
        }
        const buttonContinue = stepperLayout.createContinueButton();
        addlComponents.push(buttonContinue);

        SlideoutDecorator.decorateAndSlideIn(stepperLayout, {
            title: "Reverse Transfer to Settlement",
            fillVerticalSpace: true,
            width: "60%",
            layoutLoadListeners: () => {
                stepperLayout.reverseTransferSettlementStepper.addBeforeStepListener((event: StepEvent) => {
                    stepperLayout.doBeforeStep(event, buttonContinue, props);
                });

                stepperLayout.reverseTransferSettlementEditStep.getParentLayout().busy = true;
                stepperLayout.setupWarningsAndExceptionsLayout(props)
                    .then((reverseTransferSettlementWarnings) => {
                        stepperLayout.doBeforeStepLayoutDisplay();
                        reverseTransferSettlementWarnings.validateReversals(props)
                            .then((editList: ReportData[]) => {
                                stepperLayout.displayEditResults(editList, buttonContinue,
                                    reverseTransferSettlementWarnings);
                                stepperLayout.reverseTransferSettlementEditStep.getParentLayout().busy = false;
                            })
                            .finally(() => {
                                stepperLayout.reverseTransferSettlementEditStep.getParentLayout().busy = false;
                            });
                    }).catch(reason => {
                        CommonDialogs.showError(reason);
                    })
            },
            addlComponents: addlComponents,
            overlayProps: { closeOnClickOff: false, greyedBackground: true }
        });
    }
}
